import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom'

import AccountPage from '../pages/AccountPage'
import {
  CallbackSignIn,
  CallbackSignInApp,
  CallbackSignOut,
  OnboardingCallback,
} from '../pages/AuthPages/'
import FavoritesPage from '../pages/FavotitesPage'
import HomePage from '../pages/HomePage'
import { OnboardingPage } from '../pages/OnboardingPage'
import Page404 from '../pages/Page404'
import { PricingCheckoutPage } from '../pages/PricingCheckoutPage'
import RecentPage from '../pages/RecentPage'
import SearchPage from '../pages/SearchPage'
import SessionPage from '../pages/SessionPage'
import SubscriptionPage from '../pages/SubscriptionPage'
import { TwinGenerationPage } from '../pages/TwinGenerationPage'

import ProtectedRoute from '../components/Auth/ProtectedRoute'
import Header from '../components/Navigation/Header'
import QuestionForm from '../components/QuestionForm'
import { useFavoriteMentorsIds } from '../hooks/apiHooks/MentorHooks'
import { useMentorsState } from '../hooks/storeHooks/useMentors'
import { analytics } from '../utils/analytics'

const { MODE } = import.meta.env

const locationMap: Record<string, string> = {
  '/': 'featured',
  '/recent': 'recent',
  '/favorites': 'favorites',
  '/search': 'search',
  '/pricing': 'pricing',
  '/account': 'account-overview',
  '/account/billing': 'account-billing',
  '/generate-twin': 'generate',
}

function UserRouter() {
  const location = useLocation()
  const auth = useAuth()
  const isAuthParams = hasAuthParams()
  const [hasTriedSignin, setHasTriedSignin] = useState(false)
  const { setFavoriteMentors } = useMentorsState()
  const [searchParams] = useSearchParams()
  const platformParam = searchParams.get('platform')

  const { data: favoriteMentorsData, refetch } = useFavoriteMentorsIds()

  const isDevelopment = MODE === 'development'

  useEffect(() => {
    if (
      !isAuthParams &&
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !hasTriedSignin &&
      location.pathname !== '/macos-onboarding'
    ) {
      void auth.signinSilent()
      setHasTriedSignin(true)
    }

    if (auth.isAuthenticated) {
      void refetch()
    }
  }, [auth, isAuthParams, hasTriedSignin])

  const showForm = useMemo(() => {
    const allowedPaths = ['/', '/recent', '/favorites', '/search']
    return allowedPaths.includes(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    if (favoriteMentorsData) {
      setFavoriteMentors(favoriteMentorsData.mentorIds)
    }
  }, [favoriteMentorsData])

  // Log page view events to analytics exclude session pages
  useEffect(() => {
    if (locationMap[location.pathname]) {
      const event_properties = {
        page_name: locationMap[location.pathname],
      }
      analytics.logEvent('page_viewed', event_properties)
    }
  }, [location.pathname])

  const LazyIcons = lazy(() => import('../components/Icons/AllIcons'))

  const isLocalDev = window.location.href.includes('localhost')

  const showHeader = useMemo(
    () =>
      !/^(\/callback|\/generate-twin|\/onboarding|\/macos-onboarding)/.test(
        location.pathname,
      ) && platformParam !== 'macos',
    [location.pathname, platformParam],
  )

  return (
    <div className="min-h-screen" id="main-container">
      {showHeader && platformParam !== 'macos' && <Header />}
      <Routes>
        <Route path="/callbackSignIn" element={<CallbackSignIn />} />
        <Route path="/callbackSignInApp" element={<CallbackSignInApp />} />
        <Route path="/callbackSignOut" element={<CallbackSignOut />} />
        <Route path="/onboardingcallback" element={<OnboardingCallback />} />
        <Route path="/" element={<HomePage />} />
        <Route
          path="/onboarding"
          element={<Navigate to="/macos-onboarding" replace />}
        />
        <Route path="/macos-onboarding" element={<OnboardingPage />} />
        <Route path="/:slug/profile" element={<HomePage />} />
        <Route path="/:slug" element={<SessionPage />} />
        <Route
          path="/account/*"
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          }
        />
        <Route path="/pricing" element={<SubscriptionPage />} />
        <Route
          path="/pricing-checkout"
          element={
            <ProtectedRoute>
              <PricingCheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route path="/not-found" element={<Page404 />} />
        <Route path="*" element={<Page404 />} />

        <Route
          path="/favorites"
          element={
            <ProtectedRoute>
              <FavoritesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/favorites/:slug/profile"
          element={
            <ProtectedRoute>
              <FavoritesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <SearchPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/recent"
          element={
            <ProtectedRoute>
              <RecentPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/generate-twin/*"
          element={
            <ProtectedRoute>
              <TwinGenerationPage />
            </ProtectedRoute>
          }
        />

        {isDevelopment && (
          <>
            <Route
              path="/recent/:cardId"
              element={
                <ProtectedRoute>
                  <RecentPage />
                </ProtectedRoute>
              }
            />

            {isLocalDev && (
              <Route
                path="/dev-icons"
                element={
                  <Suspense>
                    <LazyIcons />
                  </Suspense>
                }
              />
            )}
          </>
        )}
      </Routes>
      {showForm && <QuestionForm />}
    </div>
  )
}

export default UserRouter
